import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface DenemeDers {
  id: string;
  ogrenci_id: string;
  ogretmen_id: string;
  brans: string | null;
  planlanan_tarih: string | null;
  planlanan_saat: string | null;
  planlanan_sure: string | null;
  gerceklesen_sure: string | null;
  durum: string | null;
  sonuc: string | null;
  veli_memnun: boolean | null;
  ogrenci_devam_istiyor: boolean | null;
  tavsiye_edilen_haftalik_ders: number | null;
  ogretmen_degerlendirme_notu: string | null;
  yoneticiye_not: string | null;
  ogrenci?: { ogrenci_adi: string; veli_adi: string | null; telefon: string | null };
  ogretmen?: { ad_soyad: string };
}

const statusColors: Record<string, string> = {
  Planlandı: 'bg-blue-100 text-blue-700',
  Yapıldı: 'bg-green-100 text-green-700',
  'Öğrenci Katılmadı': 'bg-orange-100 text-orange-700',
  Ertelendi: 'bg-yellow-100 text-yellow-700',
  İptal: 'bg-red-100 text-red-700',
};

const resultColors: Record<string, string> = {
  'Aktif Derse Döndü': 'bg-green-100 text-green-700',
  Kararsız: 'bg-yellow-100 text-yellow-700',
  'Devam Etmeyecek': 'bg-red-100 text-red-700',
  'Tekrar Aranacak': 'bg-blue-100 text-blue-700',
};

const STATUSES = ['Planlandı', 'Yapıldı', 'Öğrenci Katılmadı', 'Ertelendi', 'İptal'];
const RESULTS = ['Aktif Derse Döndü', 'Kararsız', 'Devam Etmeyecek', 'Tekrar Aranacak'];

export default function DenemeDersleriPage() {
  const [data, setData] = useState<DenemeDers[]>([]);
  const [teachers, setTeachers] = useState<{id: string; ad_soyad: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState<DenemeDers | null>(null);
  const [deleteItem, setDeleteItem] = useState<DenemeDers | null>(null);
  const [saving, setSaving] = useState(false);

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    await supabase.from('egtpanel_deneme_dersleri').delete().eq('id', deleteItem.id);
    setSaving(false);
    setDeleteItem(null);
    fetchData();
  };

  const fetchData = useCallback(async () => {
    const { data: rows } = await supabase
      .from('egtpanel_deneme_dersleri')
      .select('*, ogrenci:egtpanel_ogrenciler(ogrenci_adi, veli_adi, telefon), ogretmen:egtpanel_ogretmenler(ad_soyad)')
      .order('planlanan_tarih', { ascending: false });
    setData(rows || []);
    const { data: t } = await supabase.from('egtpanel_ogretmenler').select('id, ad_soyad').order('ad_soyad');
    setTeachers(t || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter(d => {
    const q = search.toLowerCase();
    const ogrenciAdi = d.ogrenci?.ogrenci_adi?.toLowerCase() || '';
    const ogretmenAdi = d.ogretmen?.ad_soyad?.toLowerCase() || '';
    const matchQ = !q || ogrenciAdi.includes(q) || ogretmenAdi.includes(q);
    const matchStatus = !filterStatus || d.durum === filterStatus;
    const matchTeacher = !filterTeacher || d.ogretmen_id === filterTeacher;
    return matchQ && matchStatus && matchTeacher;
  });

  const handleResultSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editItem) return;
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    await supabase.from('egtpanel_deneme_dersleri').update({
      durum: fd.get('status') as string,
      sonuc: fd.get('result') as string || null,
      gerceklesen_sure: fd.get('actualDuration') as string || null,
      ogretmen_degerlendirme_notu: fd.get('teacherNote') as string || null,
      yoneticiye_not: fd.get('adminNote') as string || null,
      veli_memnun: fd.get('parentSatisfied') === 'true',
      ogrenci_devam_istiyor: fd.get('studentWantsContinue') === 'true',
      tavsiye_edilen_haftalik_ders: Number(fd.get('recommendedWeeklyLessons')) || null,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', editItem.id);
    setSaving(false);
    setEditItem(null);
    fetchData();
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Deneme Dersleri</h1>
        <p className="text-slate-500 text-sm mt-1">Tüm deneme derslerini takip edin ve sonuçları işleyin</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam', val: data.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Planlandı', val: data.filter(d => d.durum === 'Planlandı').length, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Yapıldı', val: data.filter(d => d.durum === 'Yapıldı').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Derse Dönen', val: data.filter(d => d.sonuc === 'Aktif Derse Döndü').length, color: 'text-emerald-700', bg: 'bg-emerald-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Öğrenci veya öğretmen ara..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Durumlar</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterTeacher} onChange={e => setFilterTeacher(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Öğretmenler</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.ad_soyad}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['Tarih / Saat', 'Öğrenci', 'Veli / Tel', 'Branş', 'Öğretmen', 'Süre', 'Durum', 'Sonuç', 'İşlem'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16">
                  <i className="ri-calendar-2-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Deneme dersi bulunamadı</p>
                </td></tr>
              ) : filtered.map(d => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-slate-800">{d.planlanan_tarih || '—'}</div>
                    <div className="text-xs text-slate-400">{d.planlanan_saat?.slice(0,5) || '—'}</div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{d.ogrenci?.ogrenci_adi || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                    <div>{d.ogrenci?.veli_adi || '—'}</div>
                    <div className="text-xs text-slate-400">{d.ogrenci?.telefon || '—'}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">{d.brans || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{d.ogretmen?.ad_soyad || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600 text-xs">
                    <div>Plan: {d.planlanan_sure || '—'}</div>
                    {d.gerceklesen_sure && <div>Ger: {d.gerceklesen_sure}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[d.durum || ''] || 'bg-slate-100 text-slate-500'}`}>{d.durum || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {d.sonuc ? (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${resultColors[d.sonuc] || 'bg-slate-100 text-slate-500'}`}>{d.sonuc}</span>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditItem(d)} className="text-[#1e3a5f] text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[#1e3a5f]/20 hover:bg-[#1e3a5f]/5 transition-colors cursor-pointer whitespace-nowrap">Sonuç Gir</button>
                      <button onClick={() => setDeleteItem(d)} className="text-red-500 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {deleteItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4"><i className="ri-delete-bin-line text-red-500 text-xl" /></div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Deneme Dersini Sil</h3>
            <p className="text-slate-500 text-sm mb-6"><strong>{deleteItem.ogrenci?.ogrenci_adi}</strong> deneme dersini silmek istediğinizden emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDelete} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-60 cursor-pointer">{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {editItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Deneme Dersi Sonucu</h2>
                <p className="text-sm text-slate-500">{editItem.ogrenci?.ogrenci_adi} · {editItem.planlanan_tarih}</p>
              </div>
              <button onClick={() => setEditItem(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleResultSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select name="status" defaultValue={editItem.durum || 'Planlandı'} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Gerçekleşen Süre</label>
                  <select name="actualDuration" defaultValue={editItem.gerceklesen_sure || ''} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">—</option>
                    {['20 dk', '30 dk', '40 dk', '45 dk', '60 dk'].map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              {[
                { label: 'Veli memnun kaldı mı?', name: 'parentSatisfied', val: editItem.veli_memnun },
                { label: 'Öğrenci devam etmek istiyor mu?', name: 'studentWantsContinue', val: editItem.ogrenci_devam_istiyor },
              ].map(f => (
                <div key={f.name} className="flex items-center justify-between">
                  <label className="text-sm text-slate-600">{f.label}</label>
                  <select name={f.name} defaultValue={f.val ? 'true' : 'false'} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="true">Evet</option>
                    <option value="false">Hayır</option>
                  </select>
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tavsiye Edilen Haftalık Ders</label>
                <input type="number" name="recommendedWeeklyLessons" defaultValue={editItem.tavsiye_edilen_haftalik_ders || 0} min={0} max={7} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sonuç Durumu</label>
                <select name="result" defaultValue={editItem.sonuc || ''} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                  <option value="">— Seçiniz —</option>
                  {RESULTS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Öğretmen Değerlendirme Notu</label>
                <textarea name="teacherNote" defaultValue={editItem.ogretmen_degerlendirme_notu || ''} rows={3} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Yöneticiye Not</label>
                <textarea name="adminNote" defaultValue={editItem.yoneticiye_not || ''} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a]">
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}